import {Injectable, Signal, WritableSignal, effect, inject, signal} from '@angular/core';
import {take} from 'rxjs';

import {ApiService} from './api.service';
import {DashboardLinkResource} from '../types/generated/dashboardLinkResource';
import {CustomerService} from './customer.service';
import {ProfileService} from './profile.service';

export type DashboardLinksLoadState = 'idle' | 'loading' | 'loaded' | 'error';

@Injectable({
  providedIn: 'root'
})
export class DashboardLinksStateService {
  private apiService: ApiService = inject(ApiService);
  private customerService: CustomerService = inject(CustomerService);
  private profileService: ProfileService = inject(ProfileService);

  private linksState: WritableSignal<DashboardLinkResource[]> = signal<DashboardLinkResource[]>([]);
  public readonly links: Signal<DashboardLinkResource[]> = this.linksState.asReadonly();

  private loadStateSignal: WritableSignal<DashboardLinksLoadState> = signal<DashboardLinksLoadState>('idle');
  public readonly loadState: Signal<DashboardLinksLoadState> = this.loadStateSignal.asReadonly();

  private loadErrorSignal: WritableSignal<unknown | null> = signal<unknown | null>(null);
  public readonly loadError: Signal<unknown | null> = this.loadErrorSignal.asReadonly();

  private lastContextKey: string | null = null;

  public constructor() {
    effect(() => {
      const customerKey = this.customerService.customerKey();
      const userId = this.profileService.profile()?.user.id ?? null;
      const nextContextKey = `${customerKey ?? ''}|${userId ?? ''}`;

      if (this.lastContextKey === null) {
        this.lastContextKey = nextContextKey;
        return;
      }

      if (this.lastContextKey !== nextContextKey) {
        this.reset();
        this.lastContextKey = nextContextKey;
      }
    });
  }

  public reset(): void {
    this.linksState.set([]);
    this.loadStateSignal.set('idle');
    this.loadErrorSignal.set(null);
  }

  public loadLinks(force = false): void {
    const state = this.loadState();

    if (state === 'loading') {
      return;
    }

    if (!force && state !== 'idle') {
      return;
    }

    this.loadStateSignal.set('loading');
    this.loadErrorSignal.set(null);

    this.apiService.getDashboardLinks()
      .pipe(take(1))
      .subscribe({
        next: (links) => {
          this.linksState.set(links ?? []);
          this.loadStateSignal.set('loaded');
          this.loadErrorSignal.set(null);
        },
        error: (error) => {
          console.error(error);
          this.loadStateSignal.set('error');
          this.loadErrorSignal.set(error);
        }
      });
  }
}
